import React, { useState, useEffect, useRef } from 'react';
import {
  FiX,
  FiSmile,
  FiCoffee,
  FiCloudLightning,
  FiHash,
  FiUserCheck,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import Copilot from '../core/copilot/Copilot';
import Editor from '../core/Editor';
import {
  API_BASE_URL,
  loadCopilotConversation,
  updateCopilotMessages,
  toastdisplay,
  setLocalStorageWithSync,
} from '../utils/utils';
import { getStoredUser } from '../utils/authSession';
import './GetStartedWith.css';

const GetStartedWith = ({
  title = 'Get started with',
  spaceName = 'General',
  humanCoachTitle = 'Talk to a coach',
  liwazaAITitle = 'Ask AI',
  communityTitle = 'Ask other students',
  showHumanCoach = true,
  showLiwazaAI = true,
  style = {},
  showCommunity = true,
  aiOnly = false,
  onAskHumanCoach,
  onAskLiwazaAI,
  onAskCommunity,
  aifirstmessage = '',
  aicontext = '',
}) => {
  // Product policy: only AI help entry points should be shown.
  const aiOnlyMode = true;
  const navigate = useNavigate();
  const [user] = useState(() => getStoredUser());
  const [showPostModal, setShowPostModal] = useState(false);
  const [postType, setPostType] = useState(''); // 'coach' or 'community'
  const [postTitle, setPostTitle] = useState('');
  const [postContent, setPostContent] = useState('');
  const [selectedSpace, setSelectedSpace] = useState(spaceName);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [coachCount, setCoachCount] = useState(0);
  const [studentCount, setStudentCount] = useState(() => {
    // Initialize student count from cache if available and valid
    const cachedData = localStorage.getItem('studentCountCache');
    const now = Date.now();
    const cacheValidityMs = 5 * 60 * 1000; // 5 minutes in milliseconds

    if (cachedData) {
      try {
        const { count, timestamp } = JSON.parse(cachedData);

        // Check if cache is still valid (less than 5 minutes old)
        if (now - timestamp < cacheValidityMs) {
          return count;
        }
      } catch (error) {
        console.warn('Invalid student count cache data:', error);
      }
    }

    // Return 0 as default, will be set by generateRandomCounts
    return 0;
  });

  // Copilot states
  const [copilotVisible, setCopilotVisible] = useState(false);
  const [copilotMessages, setCopilotMessages] = useState(() => {
    // Try to load saved conversation first using the same key system as the main Copilot
    const savedMessages =
      user?.email && spaceName
        ? loadCopilotConversation(user.email, spaceName)
        : [];

    return savedMessages;
  });
  const [generating, setGenerating] = useState(false);
  const [context, setContext] = useState(aicontext ? aicontext : '');
  const messagesEndRef = useRef(null);
  const spacesDataLoaded = useRef(false);
  const fetchingSpaces = useRef(false);

  // Toast and spaces states for backend API calls
  const [toast, setToast] = useState({
    show: false,
    message: '',
    success: true,
  });
  const [spaces, setSpaces] = useState(() => {
    // Initialize spaces from localStorage if available
    try {
      const cachedSpaces = localStorage.getItem('liwaza_spaces');
      if (cachedSpaces) {
        spacesDataLoaded.current = true; // Mark as loaded if we have cached data
        return JSON.parse(cachedSpaces);
      }
    } catch (error) {
      console.warn('Error loading spaces from localStorage:', error);
    }
    return [];
  });

  // Fetch spaces from backend (simplified version from Community.js)
  const fetchSpaces = async () => {
    // Check if already loaded or currently fetching
    if (spacesDataLoaded.current) {
      console.log('Spaces already loaded, skipping fetch');
      return;
    }

    if (fetchingSpaces.current) {
      console.log('Spaces fetch already in progress, skipping duplicate call');
      return;
    }

    // Only fetch from backend if spaces are not already loaded from localStorage
    if (spaces.length > 0) {
      console.log(
        'Spaces already loaded from localStorage, skipping backend fetch'
      );
      spacesDataLoaded.current = true;
      return;
    }

    try {
      fetchingSpaces.current = true;
      console.log('Fetching spaces from backend...');

      const response = await fetch(`${API_BASE_URL}/api/spaces`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.success && Array.isArray(data.spaces)) {
        const formattedSpaces = data.spaces.map((space) => ({
          ...space,
          items: space.items.map((item) => ({
            ...item,
            long_name: item.long_name || item.name,
            display_name: item.long_name || item.name,
          })),
        }));
        setSpaces(formattedSpaces);

        // Save to localStorage with sync
        await setLocalStorageWithSync(
          'liwaza_spaces',
          JSON.stringify(formattedSpaces)
        );

        spacesDataLoaded.current = true;
        console.log('✅ Spaces loaded successfully');
      }
    } catch (error) {
      console.error('❌ Error fetching spaces:', error);
    } finally {
      fetchingSpaces.current = false;
    }
  };

  // Generate random counts for engagement with caching
  const generateRandomCounts = () => {
    const envCoachCount = process.env.REACT_APP_COACH_COUNT || '15';
    setCoachCount(parseInt(envCoachCount)); // From .env.development

    // Check if we have cached student count that's still valid (5 minutes)
    const cachedData = localStorage.getItem('studentCountCache');
    const now = Date.now();
    const cacheValidityMs = 5 * 60 * 1000; // 5 minutes in milliseconds

    if (cachedData) {
      try {
        const { count, timestamp } = JSON.parse(cachedData);

        // Check if cache is still valid (less than 5 minutes old)
        if (now - timestamp < cacheValidityMs) {
          setStudentCount(count);
          return;
        }
      } catch (error) {
        console.warn('Invalid student count cache data:', error);
      }
    }

    // Generate new random count and cache it
    const newStudentCount = Math.floor(Math.random() * 196) + 5; // 5-200 students
    setStudentCount(newStudentCount);

    // Store in localStorage with timestamp
    setLocalStorageWithSync(
      'studentCountCache',
      JSON.stringify({
        count: newStudentCount,
        timestamp: now,
      })
    );
  };

  const handleAskHumanCoach = () => {
    if (onAskHumanCoach) {
      onAskHumanCoach();
    } else {
      generateRandomCounts();
      setPostType('coach');
      setShowPostModal(true);
    }
  };

  const handleAskLiwazaAI = () => {
    if (onAskLiwazaAI) {
      onAskLiwazaAI();
    } else {
      // Use custom first message if provided, otherwise default
      const initialMessage =
        aifirstmessage || `I need help with ${spaceName}. Can you assist me?`;

      // Get user's first name from sessionStorage or use a default
      const userFirstName = user?.first_name || 'there';

      // Add welcome message (keep it simple)
      if (copilotMessages.length === 0) {
        const welcomeMessage = {
          role: 'ai',
          content: `Hi ${userFirstName}, how can I help you today?`,
        };

        setCopilotMessages((prev) => [...prev, welcomeMessage]);
      }

      setContext(initialMessage); // Keep initial message in textarea
      setCopilotVisible(true);
    }
  };

  const handleAskCommunity = () => {
    if (onAskCommunity) {
      onAskCommunity();
    } else {
      generateRandomCounts();
      setPostType('community');
      setShowPostModal(true);
    }
  };

  const handleSubmitPost = async () => {
    if (!postContent.trim()) {
      toastdisplay(
        'Please enter some content for your post',
        'error',
        3000,
        setToast
      );
      return;
    }

    if (!postTitle.trim()) {
      toastdisplay(
        'Please enter a title for your post',
        'error',
        3000,
        setToast
      );
      return;
    }

    // Authentication Check
    if (!user?.email) {
      toastdisplay(
        'Please log in to create posts. Redirecting to login in 3 seconds...',
        'error',
        3000,
        setToast
      );
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      return;
    }

    setIsSubmitting(true);

    try {
      // Find the space item ID for the selected space
      let spaceItemId = null;

      // Helper function to normalize strings for comparison
      const normalizeString = (str) =>
        str.toLowerCase().replace(/\s+/g, ' ').trim();

      // Helper function to find space by exact or similar match
      const findSpaceItem = (spaceName) => {
        const normalizedSpaceName = normalizeString(spaceName);

        for (const space of spaces) {
          // Try exact match first (name, long_name, display_name)
          let spaceItem = space.items.find(
            (item) =>
              item.name === spaceName ||
              item.long_name === spaceName ||
              item.display_name === spaceName
          );

          if (spaceItem) {
            return spaceItem;
          }

          // Try normalized exact match
          spaceItem = space.items.find(
            (item) =>
              normalizeString(item.name) === normalizedSpaceName ||
              normalizeString(item.long_name || '') === normalizedSpaceName ||
              normalizeString(item.display_name || '') === normalizedSpaceName
          );

          if (spaceItem) {
            return spaceItem;
          }

          // Try partial match (contains)
          spaceItem = space.items.find(
            (item) =>
              normalizeString(item.name).includes(normalizedSpaceName) ||
              normalizedSpaceName.includes(normalizeString(item.name)) ||
              (item.long_name &&
                normalizeString(item.long_name).includes(
                  normalizedSpaceName
                )) ||
              (item.long_name &&
                normalizedSpaceName.includes(normalizeString(item.long_name)))
          );

          if (spaceItem) {
            return spaceItem;
          }
        }

        return null;
      };

      // Try to find the space item
      const foundSpaceItem = findSpaceItem(selectedSpace);
      if (foundSpaceItem) {
        spaceItemId = foundSpaceItem.id;
        console.log(
          `Found space item: ${foundSpaceItem.name} (ID: ${spaceItemId}) for "${selectedSpace}"`
        );
      } else {
        console.warn(
          `No space item found for "${selectedSpace}". Available spaces:`,
          spaces.flatMap((space) => space.items.map((item) => item.name))
        );
      }

      const response = await fetch(`${API_BASE_URL}/api/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: postTitle,
          content: postContent,
          user_email: user.email,
          space_item_id: spaceItemId,
          help: true,
          pinned: 0, // Regular posts are not pinned by default
          tags: [], // No tags for GetStartedWith posts
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create post');
      }

      // eslint-disable-next-line no-unused-vars
      const result = await response.json();

      // Close the modal and reset form
      setPostTitle('');
      setPostContent('');
      setShowPostModal(false);
      setPostType('');
      setSelectedSpace(spaceName);

      toastdisplay(
        "Question submitted successfully! You'll receive a notification when someone replies to your post.",
        'success',
        3000,
        setToast
      );
    } catch (error) {
      console.error('Error creating question:', error);
      toastdisplay(`Error: ${error.message}`, 'error', 3000, setToast);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowPostModal(false);
    setPostType('');
    setPostTitle('');
    setPostContent('');
    setSelectedSpace(spaceName);
  };

  // Custom setMessages that also saves to localStorage using utils
  // eslint-disable-next-line no-unused-vars
  const updateMessagesLocal = (updateFunctionOrMessages) => {
    updateCopilotMessages(
      setCopilotMessages,
      updateFunctionOrMessages,
      user?.email,
      spaceName,
      'GetStartedWith',
      null, // step
      {
        spaceName: spaceName,
        currentPage: 'GetStartedWith',
      }
    );
  };

  const handleFileUpload = (file) => {
    console.log('File upload not implemented yet:', file);
  };

  const handleImageUpload = (image) => {
    console.log('Image upload not implemented yet:', image);
  };

  const editorStyles = {
    createPost: {
      wrapper: {
        minHeight: '200px',
        border: 'none',
        borderRadius: '8px',
        backgroundColor: 'var(--community-surface)',
      },
      editorContent: `
        .tiptap {
          min-height: 150px !important;
          max-height: 300px;
          background: transparent;
        }
      `,
    },
    comment: {
      wrapper: {
        minHeight: '100px',
        border: '1px solid var(--community-border)',
        borderRadius: '6px',
        backgroundColor: 'var(--community-surface)',
      },
      editorContent: `
        .tiptap {
          min-height: 80px !important;
          max-height: 200px;
          background: transparent;
        }
      `,
    },
    conversation: {
      wrapper: {
        minHeight: '80px',
        border: 'none',
        borderRadius: '6px',
        backgroundColor: 'var(--community-surface)',
      },
      editorContent: `
        .tiptap {
          min-height: 60px !important;
          max-height: 150px;
          background: transparent;
        }
      `,
    },
  };

  // Fetch spaces on component mount (only once)
  useEffect(() => {
    if (!spacesDataLoaded.current && !fetchingSpaces.current) {
      fetchSpaces();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize counts on component mount
  useEffect(() => {
    // Set coach count from environment variable
    const envCoachCount = process.env.REACT_APP_COACH_COUNT || '15';
    setCoachCount(parseInt(envCoachCount));

    // Initialize student count if not already set from cache
    if (studentCount === 0) {
      const cachedData = localStorage.getItem('studentCountCache');
      const now = Date.now();
      const cacheValidityMs = 5 * 60 * 1000; // 5 minutes in milliseconds

      let needNewCount = true;

      if (cachedData) {
        try {
          const { count, timestamp } = JSON.parse(cachedData);

          // Check if cache is still valid (less than 5 minutes old)
          if (now - timestamp < cacheValidityMs) {
            setStudentCount(count);
            needNewCount = false;
          }
        } catch (error) {
          console.warn('Invalid student count cache data:', error);
        }
      }

      if (needNewCount) {
        const newStudentCount = Math.floor(Math.random() * 196) + 5; // 5-200 students
        setStudentCount(newStudentCount);

        // Store in localStorage with timestamp
        setLocalStorageWithSync(
          'studentCountCache',
          JSON.stringify({
            count: newStudentCount,
            timestamp: now,
          })
        );
      }
    }
  }, [studentCount]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [copilotMessages]);

  return (
    <>
      {/* Toast Notifications */}
      {toast.show && (
        <div
          className={`toast-notification ${toast.success ? 'success' : 'error'}`}
        >
          <span>{toast.success ? '✔️' : '❌'}</span>
          <span>{toast.message}</span>
        </div>
      )}

      <div
        className="get-started-with"
        style={{
          // Apply passed styles with higher specificity
          ...(style || {}),
        }}
        ref={(el) => {
          // Apply styles with !important using direct DOM manipulation for guaranteed override
          if (el && style) {
            Object.entries(style).forEach(([property, value]) => {
              // Convert camelCase to kebab-case for CSS properties
              const cssProperty = property
                .replace(/([A-Z])/g, '-$1')
                .toLowerCase();
              el.style.setProperty(cssProperty, value, 'important');
            });
          }
        }}
      >
        <h3 className="get-started-title">{title}</h3>
        <div className="get-started-actions">
          {showCommunity && !aiOnly && !aiOnlyMode && (
            <button
              className="get-started-action highlighted"
              onClick={handleAskCommunity}
            >
              <div className="action-icon">
                <FiSmile />
              </div>
              <span className="action-text">{communityTitle}</span>
            </button>
          )}

          {showHumanCoach && !aiOnly && !aiOnlyMode && (
            <button
              className="get-started-action"
              onClick={handleAskHumanCoach}
            >
              <div className="action-icon">
                <FiCoffee />
              </div>
              <span className="action-text">{humanCoachTitle}</span>
            </button>
          )}

          {showLiwazaAI && (
            <button className="get-started-action" onClick={handleAskLiwazaAI}>
              <div className="action-icon">
                <FiCloudLightning />
              </div>
              <span className="action-text">{liwazaAITitle}</span>
            </button>
          )}
        </div>
      </div>

      {/* Post Modal */}
      {showPostModal && (
        <div className="get-started-modal-overlay" onClick={handleCloseModal}>
          <div
            className="create-post-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="modal-header-community">
              <h2 className="modal-title-community">
                {postType === 'coach' ? humanCoachTitle : communityTitle}
              </h2>
              <div className="modal-actions-community">
                <button
                  className="modal-action-btn-community"
                  onClick={handleCloseModal}
                >
                  <FiX size={16} />
                </button>
              </div>
            </div>

            {/* Engagement Banner */}
            <div className="get-started-engagement-banner">
              {postType === 'coach' ? (
                <div className="engagement-message coach-message">
                  <div className="coach-avatars">
                    <div className="get-started-coach-avatar">
                      <FiUserCheck size={16} />
                    </div>
                  </div>
                  <span>
                    <strong>{coachCount} coaches</strong> are waiting to reply
                    to you
                  </span>
                </div>
              ) : (
                <div className="engagement-message student-message">
                  <div className="student-indicator">
                    <div className="online-dot"></div>
                  </div>
                  <span>
                    <strong>{studentCount} other students</strong> are connected
                    and would be happy to reply to your question
                  </span>
                </div>
              )}
            </div>

            {/* Modal Content */}
            <div className="modal-content-community">
              <div className="post-form">
                <input
                  type="text"
                  placeholder={`The title of your question...`}
                  value={postTitle}
                  onChange={(e) => setPostTitle(e.target.value)}
                  className="post-title-input"
                />
                <Editor
                  value={postContent}
                  onChange={setPostContent}
                  customStyles={editorStyles.createPost}
                  placeholder="What's on your mind? Share your thoughts and question..."
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="modal-footer-community">
              <div className="get-started-footer-content">
                <div className="get-started-modal-info">
                  <FiHash size={16} />
                  <span>
                    {postType === 'coach'
                      ? `Will be posted to `
                      : `Will be posted to `}
                    <strong>{selectedSpace}</strong>
                    {postType === 'coach'
                      ? ` channel for coaches`
                      : ` channel in community`}
                  </span>
                </div>

                <button
                  className="publish-btn-community"
                  onClick={handleSubmitPost}
                  disabled={
                    !postTitle.trim() || !postContent.trim() || isSubmitting
                  }
                >
                  {isSubmitting ? (
                    <>
                      <div
                        className="community-loading-spinner"
                        style={{
                          width: '16px',
                          height: '16px',
                          marginRight: '8px',
                        }}
                      ></div>
                      Publishing...
                    </>
                  ) : (
                    'Publish'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Copilot */}
      {copilotVisible && (
        <Copilot
          messages={copilotMessages}
          generating={generating}
          generatingText={`Thinking`}
          context={context}
          aiContext={aicontext}
          setContext={setContext}
          handleFileUpload={handleFileUpload}
          handleImageUpload={handleImageUpload}
          selectedFile={null}
          selectedImage={null}
          messagesEndRef={messagesEndRef}
          setMessages={setCopilotMessages}
          setGenerating={setGenerating}
          currentPage="GetStartedWith"
          userInfo={{
            spaceName: spaceName,
            currentPage: 'GetStartedWith',
          }}
          sessionContext={{
            spaceName: spaceName,
            getStartedWith: true,
          }}
          onClose={() => setCopilotVisible(false)}
          showFileUpload={false}
          showImageUpload={false}
        />
      )}
    </>
  );
};

export default GetStartedWith;
